<script setup lang="ts">
import { ref, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import type { EventType } from '@interfaces/event_type';
import Form from './Form.vue';
import Breadcrumbs from './util/Breadcrumbs.vue';

const router = useRouter();
const { t } = useI18n();

// Get the current instance to access global properties
const instance = getCurrentInstance();

const event = ref<EventType>({} as EventType);

const createEvent = async (
  eventData: EventType | FormData,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
) => {
  try {
    const url = '/api/v1/events/create';

    // Check if we're sending FormData (for file uploads) or JSON
    const isFormData = eventData instanceof FormData;

    const response = await axios.post(url, eventData, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' }
    });

    if (onSuccess) onSuccess(response);
  } catch (error: any) {
    console.error('Error creating event:', error);
    if (onError) onError(error);
  }
};

const handleSaveSuccess = () => {
  // Show success message using global createToast
  const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
  const successMessage = t('events.create_success');

  if (createToast) {
    createToast(successMessage, {
      type: 'success',
      position: 'top-center',
      timeout: 3000,
      showCloseButton: true,
    });
  }

  // Navigate to events list page after a short delay
  setTimeout(() => {
    router.push({ name: 'list' }).then(() => {
    }).catch((error) => {
      console.error('Navigation failed:', error);
    });
  }, 1500); // Slightly longer delay to show the success message
};

onMounted(() => {
  // Add meta tag to discourage password managers
  const meta = document.createElement('meta');
  meta.name = 'password-manager-disabled';
  meta.content = 'true';
  document.head.appendChild(meta);
});

onUnmounted(() => {
  // Clean up meta tag when component unmounts
  const meta = document.querySelector('meta[name="password-manager-disabled"]');
  if (meta) {
    meta.remove();
  }
});
</script>

<template>
  <Breadcrumbs />

  <Form
    :event="event"
    @save="createEvent"
    @save-success="handleSaveSuccess"
  />
</template>
